import styled from "styled-components";

import Search from "../icons/Search";
import { TriggerButton as OverflowMenuButton } from "../OverflowMenu";

import logo from "../icons/np-logo-symbol.png";

import {
    colors,
    Grid,
    Row,
    Col,
    Section,
    Flexbox,
    A,
    mobileHeaderHeight,
    desktopHeaderHeight,
    breakpoints,
    zIndexTop,
    easingSeconds
} from "../shared";

export const Logo = styled.img.attrs({ src: logo })`
    width: 2.375rem;
    transition: opacity ${easingSeconds};

    &:hover {
        opacity: 0.6;
    }
`;

export const HeaderLogoLink = styled(A)`
    svg {
        transition: opacity ${easingSeconds};
    }
    &:hover svg {
        opacity: 0.6;
    }

    /* from mobile to desktop breakpoint: */
    @media (min-width: ${breakpoints.sm}rem) and (max-width: ${breakpoints.beforeDesktop}rem) {
        svg {
            margin-right: 1.25rem; /* 20px */
        }
    }
`;

// search
export const SearchInput = styled.input.attrs({
    type: "search"
})`
    width: 100%;
    border: 1px solid #f2f2f2;
    border-radius: 0;
    -webkit-appearance: none !important;
    font-family: inherit;
    color: ${colors.black};
    font-size: 1rem; /* 16px to avoid zoom on mobile devices */
    padding: 0.875rem 1rem 0.875rem 2.625rem;
    box-sizing: border-box !important; /* overrides main.css */

    @media (max-width: ${breakpoints.beforeTablet}rem) {
        font-size: 1rem; /* 16px to avoid zoom on mobile devices */
        padding: 0.375rem 0.625rem 0.375rem 1.75rem;
    }

    &::placeholder {
        opacity: 1;
        color: ${colors.brownishGreyTwo};
    }

    &:hover {
        border: 1px solid #d5d3d3;
        outline-color: -webkit-focus-ring-color !important;
        @media (max-width: ${breakpoints.md}rem) {
            font-size: 1rem; /* 16px to avoid zoom on mobile devices */
        }
    }

    &:focus {
        outline: -webkit-focus-ring-color auto 1px;
        outline-offset: -2px;
        font-size: 1rem; /* 16px to avoid zoom on mobile devices */

        &::placeholder {
            color: transparent;
            /* hides placeholder text on input focus */
        }
    }
`;

export const SearchIcon = styled(Search)`
    position: absolute;
    width: 0.875rem;

    /* Mobile: */
    left: 0.625rem;
    top: 0.45rem;

    @media (min-width: ${breakpoints.tabletPx}px) {
        /* REMs wasnt working on iPad mini */
        top: 0.96rem;
        left: 1rem;
    }
`;

export const SearchWrapper = styled.div`
    position: relative;
`;

// non-mobile content
export const NonMobileTopRow = styled(Row)``;

export const MainLinksCol = styled(Col)`
    transition: opacity ${easingSeconds};
    [aria-hidden="true"] {
        display: none;
    }
`;

export const ProfileMenuCol = styled(Col)`
    transition: margin-top ${easingSeconds};
`;

export const SearchContainerRow = styled(Row)`
    transition: margin ${easingSeconds};

    /* tablet+: */
    @media (min-width: ${breakpoints.md}rem) {
        ${({ isHeroSearch }) => isHeroSearch && "padding-bottom: 3px;"}

        /* prettier-ignore */
        ${Col}:nth-child(2) {
            min-height: 49px;
        }

        /* prettier-ignore */
        ${({ isHeroSearchV2 }) =>
            isHeroSearchV2 &&
            `
            ${Col}:nth-child(2) {
                min-height: 56px;
            }
        `}
    }
`;

export const TextLinkGrid = styled(Grid)`
    overflow-x: visible;
`;

export const Link = styled(A)`
    font-size: 0.875rem;

    @media (max-width: ${breakpoints.md}rem) {
        font-size: 0.75rem;
    }
`;

export const MainLinks = styled(Flexbox)`
    ${Link} {
        &:not(:last-of-type) {
            margin-right: 1rem;
        }
    }

    ${OverflowMenuButton} {
        margin-left: 1.25rem;
    }
`;

export const NonMobileContentWrapper = styled.div`
    ${NonMobileTopRow} {
        margin-bottom: 1.625rem;
        height: 24px;
    }

    ${Logo} {
        width: 5.25rem;
    }

    ${SearchWrapper} {
        width: 100%;
    }

    ${SearchIcon} {
        width: 0.875rem;
        margin-right: 0.625rem;
    }

    @media (max-width: ${breakpoints.md}rem) {
        display: none;
    }
`;

// mobile content
export const LogoAndSearch = styled(Flexbox)`
    ${Logo} {
        margin-right: 1.25rem;
    }

    ${SearchWrapper} {
        flex: 1;
    }
`;

export const MenuAndAvatar = styled(Flexbox)`
    ${OverflowMenuButton} {
        ${({ loggedOut }) =>
            loggedOut ? "margin-left: 0.75rem" : "margin-right: 0.9375rem"}
    }

    /* up to Tablet size: */
    @media (max-width: ${breakpoints.beforeTablet}rem) {
        margin-top: -2px;
    }
`;

export const MobileContentWrapper = styled(Grid)`
    display: none;

    @media (max-width: ${breakpoints.md}rem) {
        display: block;
        overflow-x: visible;
    }
`;

// site header
export const StyledSiteHeader = styled(Section)`
    padding-top: 0.9375rem;
    padding-bottom: 0.9375rem;

    ${({ transparent }) =>
        transparent && "background-color: transparent; border-bottom: 0"};

    ${({ isHeroSearch, heroSearchBgColor }) =>
        isHeroSearch &&
        `background-color: ${
            heroSearchBgColor ? heroSearchBgColor : "#FAFAFA"
        }; border-bottom: 0;`};
    /* FAFAFA is specific to current interim home page; update this when hero search is refactored for new home page/photos landing page */


    ${({ isHeroSearch, transparent }) =>
        !isHeroSearch &&
        !transparent &&
        `background-color: ${colors.whiteDuplicate}; border-bottom: 1px solid #d8d8d8;`}

    min-height: ${mobileHeaderHeight}px;
    position: fixed;
    z-index: ${zIndexTop};
    transition: background ${easingSeconds};

    ${Link} {
        transition: all ${easingSeconds};
        color: ${({ transparent, isHeroSearchV2 }) =>
            transparent || isHeroSearchV2
                ? colors.whiteDuplicate
                : colors.brownishGreyTwo};
        text-decoration: none;

        &:hover {
            color: ${({ transparent, isHeroSearchV2 }) =>
                transparent
                    ? colors.dark
                    : isHeroSearchV2
                    ? colors.veryLightPinkTwo
                    : colors.black};
            text-decoration: underline;
        }
    }

    /* for new hero search/navbar combo; 
    these styles will be cleaned up significantly when we refactor the header animations/functionality: */
    ${({ isHeroSearchV2, heroSearchBgColor }) =>
        isHeroSearchV2 &&
        `
        background-color: ${
            heroSearchBgColor ? heroSearchBgColor : colors.whiteDuplicate
        };
        border-bottom: 0;

        ${Link} {
            color: ${colors.whiteDuplicate};
        }
    `}
    //
    
    /* up to Tablet breakpoint: */
    @media (max-width: ${breakpoints.md}rem) {
        ${({ isHeroSearch }) =>
            isHeroSearch &&
            `
            ${Logo} {
                margin-top: 11px;
            }

            ${MenuAndAvatar} {
                position: relative;
                top: 5px
            }
        `}

        ${({ isHeroSearchV2 }) =>
            isHeroSearchV2 &&
            `
            ${LogoAndSearch} {
                min-height: 53px;
            }
        `}
    }
    /* Tablet + */
    @media (min-width: ${breakpoints.md}rem) {
        padding-top: 2rem;
        padding-bottom: 1.625rem;
        height: auto;
        max-height: ${desktopHeaderHeight}px;

        ${({ isHeroSearch }) =>
            isHeroSearch &&
            `
            ${Logo}{
                padding-top: 3px;
            }
        `};
    }

    /* changes to header UI when scrolling down the page: */
    &.scrolledDown {
        /* SiteHeader itself: */
        border-bottom: 1px solid #d8d8d8;
        /* TODO: add heroSearchBgColor */

        /* overflow menu: */
        ${OverflowMenuButton} path {
            fill: ${colors.brownishGrey} !important;
            /* do not do this with important; need to figure out how to make TriggerButton.js work w/o important */
        }

        /* NP Logo: */
        a svg path {
            fill: ${colors.black};
        }
    }

    /* changes to header UI when scrolled to top of page: */
    &.scrolledUp {
        /* SiteHeader itself: */
        border-bottom: 0;
        /* TODO: add heroSearchBgColor */

        /* NP logo */
        a svg path {
            fill: ${colors.whiteDuplicate};
        }
    }
`;
